// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { Button } from '@mui/material';
import { Translation } from '@shared/ui-kit/providers/translation-provider/translation';
import { type FC, useState, useCallback, useMemo } from 'react';
import { AssetOperationModal } from '@shared/portfolio-operations';
import type {
  SourceAsset,
  BuyOperationData,
} from '@shared/portfolio-operations/types';
import {
  OperationsClientGate,
  useSendToTrader,
} from '@advisor-app/features/operations';
import { useAppSelector } from '@advisor-app/store/store';
import { selectAccountCurrency } from '@shared/data-access/slices/user';
import { OperationType } from '@shared/data-access/interfaces/operation-type';

export interface BuySellButtonProps {
  ticker: string;
  operation: 'buy';
  assetType: string;
  dfiUrl?: string | null;
}

export const BuySellButton: FC<BuySellButtonProps> = ({
  ticker,
  operation,
  assetType,
  dfiUrl,
}) => {
  const { sendTradeTicker } = useSendToTrader();
  const currency = useAppSelector(selectAccountCurrency);
  const [openModal, setOpenModal] = useState(false);

  const handleOpenModal = useCallback(() => {
    setOpenModal(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setOpenModal(false);
  }, []);

  const handleBuyConfirm = useCallback(
    (data: BuyOperationData, sourceAsset: SourceAsset) => {
      sendTradeTicker({
        operation: OperationType.BUY,
        ticker: sourceAsset.ticker,
        volume: data.amount,
        contracts_number: data.shares,
        asset_type: sourceAsset.assetType,
        inputType: data.inputType,
      });
      handleCloseModal();
    },
    [sendTradeTicker, handleCloseModal],
  );

  const sourceAsset: SourceAsset = useMemo(
    () => ({
      ticker: ticker || '',
      name: ticker || '',
      assetType: assetType || 'stock',
      investment: 0,
      quantity: 0,
      currency: currency,
      isin: null,
      dfiUrl: dfiUrl,
      institution: {
        id: '',
        name: '',
      },
      accountId: '',
    }),
    [ticker, assetType, currency, dfiUrl],
  );

  if (!ticker || ticker.trim() === '') {
    return null;
  }

  return (
    <OperationsClientGate>
      <>
        <Button
          data-testid={`${ticker}-${operation}-button`}
          variant="outlined"
          color="secondary"
          size="small"
          onClick={handleOpenModal}
        >
          <Translation i18nKey="glossary.kpi-buy" ns="glossary" />
        </Button>
        {openModal && (
          <AssetOperationModal
            open={openModal}
            onClose={handleCloseModal}
            operation={OperationType.BUY}
            sourceAsset={sourceAsset}
            onBuy={handleBuyConfirm}
          />
        )}
      </>
    </OperationsClientGate>
  );
};
